import {useEffect, useMemo, useRef} from 'react';
import type {ReactNode} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import type {Dayjs} from 'dayjs';
import {Button, Form, Input, Select, TimePicker} from 'antd';
import {
    ArrowRightOutlined,
    CalendarOutlined,
    CheckOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    ShopOutlined,
} from '@ant-design/icons';
import { useAppMessage } from '@providers'
import {
    DICA_JANELAS_ENTREGA,
    PASSO_MINUTOS_ENTREGA,
    buscarUfDaFilial,
    extractNumbers,
    resolverHorasEMinutosDesabilitados,
    resolverMensagemErro,
    validarHorarioEntrega,
} from '@utils';
import {usePreCadastrarAgendamentoMutation, useParceirosQuery} from "@queries";
import {useGlobalFilial, useGlobalUser, useGuidedTour} from "@hooks";
import {GuidedTour} from "@components";
import {PRE_AGENDAMENTO_MESSAGE, RoutesFields, TRANSPORT_OPTIONS} from "@constants";
import type { AgendaExternaCadastroRequest } from '../../types';
import './style.scss';

interface PreAgendamentoFormValues {
    horaEntrega: Dayjs | null;
    tipoCaminhao: string;
    filialEntrega?: number;
    codeSupplierOrTransport: number;
    volumeCarga: string;
}

const requestDto: AgendaExternaCadastroRequest = {};

const TOTAL_CAMPOS_OBRIGATORIOS = 4;

const FORMATO_HORA = 'HH:mm';

const TIPOS_CAMINHAO_DISPONIVEIS = TRANSPORT_OPTIONS.map(opcao => opcao.label);

const listarTiposCaminhao = () => {
    const ultimo = TIPOS_CAMINHAO_DISPONIVEIS.at(-1);
    const anteriores = TIPOS_CAMINHAO_DISPONIVEIS.slice(0, -1).join(', ');
    return anteriores ? `${anteriores} e ${ultimo}` : String(ultimo ?? '');
};

const normalizarSomenteDigitos = (valor: unknown) => extractNumbers(valor) ?? '';

const renderMarcadorObrigatorio = (rotulo: ReactNode, {required}: {required: boolean}) => (
    <>
        {rotulo}
        {required && <span className="form-field__obrigatorio" aria-hidden="true">*</span>}
    </>
);

export const PreAgendamentoPage = () => {
    const {date} = useParams();
    const navigate = useNavigate();
    const [user] = useGlobalUser();
    const [filial] = useGlobalFilial();
    const [form] = Form.useForm<PreAgendamentoFormValues>();
    const message = useAppMessage();
    const preCadastrarMutation = usePreCadastrarAgendamentoMutation();
    const parceirosQuery = useParceirosQuery({isFornecedor: user.isFornecedor, codigoUsuario: user.code});
    const codeSupplierOrTransportData = parceirosQuery.data ?? [];
    const rotuloParceiro = user.isFornecedor === true ? 'Transportadora' : 'Fornecedor';
    const placeholderSelectSupplierOrTransport = user.isFornecedor === true
        ? 'Selecione a transportadora'
        : 'Selecione o fornecedor';
    const rotaAgendamentos = user.isFornecedor === true ? RoutesFields.RELATORIO_FORNECEDOR : RoutesFields.RELATORIO_TRASPORTADORA;

    const parceiroSelecionado = Form.useWatch('codeSupplierOrTransport', form);
    const horaEntregaSelecionada = Form.useWatch('horaEntrega', form);
    const volumeCargaInformado = Form.useWatch('volumeCarga', form);
    const tipoCaminhaoSelecionado = Form.useWatch('tipoCaminhao', form);

    const refSelectFornecedor = useRef(null);
    const refHoraEntrega = useRef(null);
    const refVolumeCarga = useRef(null);
    const refTipoCaminhao = useRef(null);
    const refBotaoFinalizar = useRef(null);

    const { tourAberto, abrirTour, fecharTour } = useGuidedTour('pre-agendamento');

    const passosTour = useMemo(() => [
        {
            title: 'Selecione o parceiro',
            description: 'Comece escolhendo a Transportadora ou o Fornecedor responsável pela entrega.',
            target: () => refSelectFornecedor.current,
        },
        {
            title: 'Hora de entrega',
            description: 'Informe o horário previsto para a entrega na filial.',
            target: () => refHoraEntrega.current,
        },
        {
            title: 'Volume da carga',
            description: 'Digite o volume total da carga que será entregue.',
            target: () => refVolumeCarga.current,
        },
        {
            title: 'Tipo de caminhão',
            description: 'Selecione o tipo de caminhão usado no transporte. É permitido apenas um tipo por agendamento.',
            target: () => refTipoCaminhao.current,
        },
        {
            title: 'Finalizar agendamento',
            description: 'Após preencher todos os campos, clique aqui para concluir o pré-agendamento.',
            target: () => refBotaoFinalizar.current,
        },
    ], []);

    useEffect(() => {
        if (parceirosQuery.error) {
            message.error("Erro ao buscar lista da Trasportadoras/Fornecedores:");
        }
    }, [parceirosQuery.error]);

    const dataReserva = dayjs(date, 'DD-MM-YYYY').locale('pt-br');
    const isDataReservaValida = dataReserva.isValid();
    const dataReservaExtenso = isDataReservaValida ? dataReserva.format('dddd, D [de] MMMM [de] YYYY') : 'data não informada';
    const dataReservaCurta = isDataReservaValida ? dataReserva.format('DD/MM/YYYY — dddd') : 'não informada';
    const ufFilialSelecionada = buscarUfDaFilial(filial);
    const nomeParceiroSelecionado = codeSupplierOrTransportData.find(opcao => String(opcao.value) === String(parceiroSelecionado ?? ''))?.label;

    const isParceiroPreenchido = Boolean(parceiroSelecionado);
    const isHoraPreenchida = Boolean(horaEntregaSelecionada);
    const isVolumePreenchido = String(volumeCargaInformado ?? '').length > 0;
    const isTipoCaminhaoPreenchido = Boolean(tipoCaminhaoSelecionado);
    const camposPreenchidos = [isParceiroPreenchido, isHoraPreenchida, isVolumePreenchido, isTipoCaminhaoPreenchido].filter(Boolean).length;

    const resolverPendencia = () => {
        if (!isParceiroPreenchido) return `Selecione ${rotuloParceiro === 'Transportadora' ? 'a transportadora' : 'o fornecedor'} para finalizar o pré-agendamento.`;
        if (!isHoraPreenchida) return 'Informe a hora de entrega para finalizar o pré-agendamento.';
        if (!isVolumePreenchido) return 'Informe o volume total da carga para finalizar o pré-agendamento.';
        if (!isTipoCaminhaoPreenchido) return 'Selecione o tipo de caminhão para finalizar o pré-agendamento.';
        return null;
    };
    const pendencia = resolverPendencia();

    const handleVoltarAgendamentos = () => navigate('/');

    const handleIrParaAgendamentos = () => navigate(rotaAgendamentos.PATH);

    const cleanForm = () => {
        const saveFields = {
            horaEntrega: form.getFieldValue('horaEntrega'),
            tipoCaminhao: form.getFieldValue('tipoCaminhao'),
            filialEntrega: form.getFieldValue('filialEntrega'),
            codeSupplierOrTransport: form.getFieldValue('codeSupplierOrTransport'),
        };
        form.resetFields();
        form.setFieldsValue(saveFields);
    }

    const onFinish = async (values: PreAgendamentoFormValues) => {
        const horaEntrega = values.horaEntrega ?? dayjs();
        const updatedRequestDto = {
            ...requestDto,
            dataCriacao: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
            dataEntrega: dayjs(date, "DD-MM-YYYY")
                .hour(horaEntrega.hour())
                .minute(horaEntrega.minute())
                .second(0)
                .format('YYYY-MM-DDTHH:mm:ss'),
            transportadoraId: user.isFornecedor ? values.codeSupplierOrTransport : user.code ?? undefined,
            codigoFornecedor: user.isFornecedor ? user.code ?? undefined : values.codeSupplierOrTransport,
            filialEntrega: Number(filial),
            tipoCaminhao: values.tipoCaminhao,
            volumeCarga: Number(values.volumeCarga)
        };

        try {
            await preCadastrarMutation.mutateAsync(updatedRequestDto);
            message.success("Cadastro finalizado com sucesso!!")
            cleanForm();
            navigate(`/`);
        } catch (error) {
            message.error(resolverMensagemErro(error, "Erro ao finalizar o agendamento"));
            navigate(`/`);
        }
    };

    const renderLinhaResumo = (chave: string, valor: string | undefined) => (
        <div className="resumo-reserva__linha">
            <span className="resumo-reserva__chave">{chave}</span>
            <span className={valor ? 'resumo-reserva__valor' : 'resumo-reserva__valor resumo-reserva__valor--pendente'}>
                {valor ?? 'Pendente'}
            </span>
        </div>
    );

    return (
        <div className="fluxo-agendamento">
            <section className="context-bar" aria-label="Contexto da reserva">
                <div className="context-bar__grupo">
                    <span className="context-bar__icone" aria-hidden="true"><CalendarOutlined/></span>
                    <span>
                        <span className="context-bar__rotulo">Reserva para</span>
                        <span className="context-bar__valor">{dataReservaExtenso}</span>
                    </span>
                    <Button type="link" size="small" onClick={handleVoltarAgendamentos}>Trocar data</Button>
                </div>
                <span className="context-bar__divisor" aria-hidden="true"/>
                <div className="context-bar__grupo">
                    <span className="context-bar__icone" aria-hidden="true"><ShopOutlined/></span>
                    <span>
                        <span className="context-bar__rotulo">Filial de entrega</span>
                        <span className="context-bar__valor">{ufFilialSelecionada ?? 'não identificada'}</span>
                    </span>
                    <Button type="link" size="small" onClick={handleVoltarAgendamentos}>Trocar filial</Button>
                </div>
            </section>

            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={renderMarcadorObrigatorio}>
                <div className="pre-agendamento">
                    <div className="pre-agendamento__principal">
                        <section className="section-card" aria-label="Parceiro responsável pela entrega">
                            <div className="section-card__head">
                                <h2 className="section-card__title">Parceiro responsável pela entrega</h2>
                                <span className="section-card__hint">Etapa 1 de 2</span>
                            </div>
                            <div className="pre-agendamento__campos">
                                <div ref={refSelectFornecedor} className="form-field form-field--larga">
                                    <Form.Item name="codeSupplierOrTransport" label={rotuloParceiro}
                                               rules={[
                                                   {required: true, message: 'Campo obrigatório!'}
                                               ]}>
                                        <Select
                                            placeholder={parceirosQuery.isLoading ? 'Carregando...' : placeholderSelectSupplierOrTransport}
                                            showSearch={{optionFilterProp: 'label'}}
                                            loading={parceirosQuery.isLoading}
                                            disabled={parceirosQuery.isLoading}
                                            options={codeSupplierOrTransportData}
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                        </section>

                        <section className="section-card" aria-label="Dados da carga">
                            <div className="section-card__head">
                                <h2 className="section-card__title">Dados da carga</h2>
                                <span className="section-card__hint">Etapa 2 de 2 · Todos os campos são obrigatórios</span>
                            </div>
                            <div className="pre-agendamento__campos">
                                <div ref={refHoraEntrega} className="form-field form-field--larga">
                                    <Form.Item name="horaEntrega" label="Hora de entrega" extra={DICA_JANELAS_ENTREGA}
                                               rules={[{required: true, message: 'Campo obrigatório!'}, {validator: validarHorarioEntrega}]}>
                                        <TimePicker
                                            placeholder={FORMATO_HORA}
                                            format={FORMATO_HORA}
                                            minuteStep={PASSO_MINUTOS_ENTREGA}
                                            disabledTime={resolverHorasEMinutosDesabilitados}
                                            hideDisabledOptions={true}
                                            needConfirm={false}
                                            showNow={false}
                                        />
                                    </Form.Item>
                                </div>
                                <div ref={refVolumeCarga} className="form-field form-field--larga">
                                    <Form.Item name="volumeCarga" label="Volume total da carga" extra="Somente números inteiros."
                                               normalize={normalizarSomenteDigitos}
                                               rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                        <Input placeholder="Volume total da carga" inputMode="numeric"/>
                                    </Form.Item>
                                </div>
                                <div ref={refTipoCaminhao} className="form-field form-field--larga">
                                    <Form.Item name="tipoCaminhao" label="Tipo de caminhão" extra={`Apenas um tipo por agendamento. Disponíveis: ${listarTiposCaminhao()}.`}
                                               rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                        <Select placeholder="Tipo de caminhão" options={TRANSPORT_OPTIONS}/>
                                    </Form.Item>
                                </div>
                            </div>
                        </section>
                    </div>

                    <aside className="pre-agendamento__apoio">
                        <section className="section-card" aria-label="Resumo da reserva">
                            <div className="section-card__head">
                                <h2 className="section-card__title">Resumo da reserva</h2>
                            </div>
                            <div className="resumo-reserva">
                                {renderLinhaResumo('Data', dataReservaCurta)}
                                {renderLinhaResumo('Filial', ufFilialSelecionada)}
                                {renderLinhaResumo(rotuloParceiro, nomeParceiroSelecionado ? String(nomeParceiroSelecionado) : undefined)}
                                {renderLinhaResumo('Hora de entrega', horaEntregaSelecionada ? dayjs(horaEntregaSelecionada).format('HH:mm') : undefined)}
                                {renderLinhaResumo('Volume', isVolumePreenchido ? `${volumeCargaInformado} volumes` : undefined)}
                                {renderLinhaResumo('Tipo de caminhão', tipoCaminhaoSelecionado || undefined)}
                            </div>
                        </section>

                        <section className="section-card" aria-label="Como funciona o pré-agendamento">
                            <div className="section-card__head">
                                <h2 className="section-card__title">Como funciona</h2>
                            </div>
                            <div className="como-funciona">
                                <p className="como-funciona__texto">{PRE_AGENDAMENTO_MESSAGE}</p>
                                <Button type="link" size="small" icon={<ArrowRightOutlined/>} onClick={handleIrParaAgendamentos}>
                                    Ir para {rotaAgendamentos.TITLE}
                                </Button>
                            </div>
                        </section>
                    </aside>
                </div>

                <div className="commit-bar">
                    <div className="commit-bar__status">
                        <span>
                            <span className="commit-bar__forte">{camposPreenchidos}</span> de <span className="commit-bar__forte">{TOTAL_CAMPOS_OBRIGATORIOS}</span> campos obrigatórios preenchidos
                        </span>
                        {pendencia ? (
                            <span className="commit-bar__pendente"><ExclamationCircleOutlined/> {pendencia}</span>
                        ) : (
                            <span className="section-card__hint"><InfoCircleOutlined/> Tudo pronto para reservar o horário.</span>
                        )}
                    </div>
                    <div className="commit-bar__acoes">
                        <Button icon={<LeftOutlined/>} onClick={handleVoltarAgendamentos}>
                            Voltar aos agendamentos
                        </Button>
                        <div ref={refBotaoFinalizar}>
                            <Button type="primary" htmlType="submit" icon={<CheckOutlined/>} loading={preCadastrarMutation.isPending} className="botao-acao-class">
                                Finalizar Agendamento
                            </Button>
                        </div>
                    </div>
                </div>
            </Form>

            <GuidedTour
                tourAberto={tourAberto}
                abrirTour={abrirTour}
                fecharTour={fecharTour}
                passos={passosTour}
            />
        </div>
    );
};
